/**
 * MockExamSelector Component
 * Displays a list of available mock exams for selection
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Target, Loader2, Trophy, RotateCcw, Play } from 'lucide-react';

// Default track display names
const TRACK_NAMES = {
  PMP: 'PMP - Project Management Professional',
  CAPM: 'CAPM - Certified Associate in PM',
  PSM: 'PSM - Professional Scrum Master',
  PSPO: 'PSPO - Professional Scrum Product Owner',
  PRINCE2: 'PRINCE2 - Foundation',
  SAFe: 'SAFe - Foundation',
  SAFE: 'SAFe - Foundation',
  ITIL4: 'ITIL 4 - Foundation'
};

const MockExamSelector = ({
  examType,
  setExamType,
  mockExams,
  loadingMockExams,
  selectedMockExam,
  setSelectedMockExam,
  loading,
  onStart,
  onBack,
  availableTracks = [] // New prop for available tracks
}) => {
  // Use provided tracks or fallback to default 4
  const tracks = availableTracks.length > 0 
    ? availableTracks 
    : ['PMP', 'CAPM', 'PSM', 'PSPO'];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
      <div className="max-w-xl mx-auto pt-8">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0047AB] to-[#003380] flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Select Mock Exam</h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                Choose one of 5 practice exams for {examType}
              </p>
            </div>

            {/* Track Selection */}
            <div className="mb-6">
              <Label className="mb-2 block">Certification Track</Label>
              <Select value={examType} onValueChange={(value) => {
                setExamType(value);
                setSelectedMockExam(null);
              }}>
                <SelectTrigger data-testid="exam-track-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tracks.map(track => (
                    <SelectItem key={track} value={track}>
                      {TRACK_NAMES[track] || track}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mock Exam List */}
            {loadingMockExams ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
              </div>
            ) : mockExams.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No mock exams available for this track yet.
              </div>
            ) : (
              <div className="space-y-3">
                {mockExams.map((exam) => {
                  const progress = exam.user_progress || {};
                  const isSelected = selectedMockExam?.mock_exam_id === exam.mock_exam_id;
                  const hasQuestions = exam.question_count > 0;
                  
                  return (
                    <div
                      key={exam.mock_exam_id}
                      onClick={() => hasQuestions && setSelectedMockExam(exam)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        !hasQuestions 
                          ? 'border-slate-200 dark:border-slate-700 opacity-50 cursor-not-allowed' 
                          : isSelected
                            ? 'border-[#0047AB] bg-blue-50 dark:bg-blue-950/30'
                            : 'border-slate-200 dark:border-slate-700 hover:border-[#0047AB]/50'
                      }`}
                      data-testid={`mock-exam-${exam.exam_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                            progress.passed
                              ? 'bg-green-100 dark:bg-green-950/50 text-green-600'
                              : progress.completed
                                ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-600'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600'
                          }`}>
                            {progress.passed ? (
                              <Trophy className="w-6 h-6" />
                            ) : (
                              exam.exam_number
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold">{exam.name}</h3>
                            <p className="text-sm text-slate-500">
                              {exam.question_count} questions • {exam.time_minutes} min
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {progress.attempts > 0 ? (
                            <>
                              <div className="flex items-center gap-2 justify-end">
                                {progress.passed && (
                                  <Badge className="bg-green-500">Passed</Badge>
                                )}
                                <span className="font-bold text-lg">
                                  {progress.best_score}%
                                </span>
                              </div>
                              <p className="text-xs text-slate-500">
                                {progress.attempts} attempt{progress.attempts !== 1 ? 's' : ''} • Best score
                              </p>
                            </>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              Not started
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Start Button */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              {selectedMockExam && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{selectedMockExam.name}</p>
                      <p className="text-sm text-slate-600">
                        {selectedMockExam.question_count} questions • {selectedMockExam.time_minutes} min • Pass: {selectedMockExam.passing_score}%
                      </p>
                    </div>
                    {selectedMockExam.user_progress?.attempts > 0 && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <RotateCcw className="w-4 h-4" />
                        Retake
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <Button 
                className="w-full h-12 bg-[#0047AB] hover:bg-[#003380] text-white rounded-xl"
                onClick={onStart}
                disabled={loading || !selectedMockExam}
                data-testid="start-exam-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Start Exam
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MockExamSelector;
